/**
 * WsConnection — multiplayer GameConnection over a WebSocket to apps/server.
 *
 *  - Binary frames: ClientMessages as plain MessagePack (what the server's strict decoder
 *    accepts), ServerMessages decoded with the shared msgpackr codec (typed arrays intact).
 *  - URL: same origin `/ws` (works through the Vite dev proxy and behind TLS), overridable with
 *    VITE_WS_URL for a separately hosted server.
 *  - Auto-reconnect with exponential backoff + jitter. Status goes back to 'connecting' while
 *    retrying; the lobby re-sends `hello` with the reconnect token on every 'open'.
 *  - Application-level ping every few seconds: measures latency and detects half-open sockets
 *    that the browser would otherwise keep "open" for minutes.
 *  - Close codes in FATAL_CLOSE_CODES (outdated client, replaced by another tab, rate limited)
 *    end the connection for good; `closeInfo` tells the UI why.
 */
import {
  type ClientMessage,
  CloseCode,
  decode,
  encodeClientMessage,
  FATAL_CLOSE_CODES,
  type ServerMessage,
} from '@friction/protocol';
import type { ConnectionStatus, GameConnection } from './connection';

export interface WsConnectionOptions {
  url?: string;
  /** First reconnect delay; doubles per failed attempt up to `maxDelayMs` (±25 % jitter). */
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Consecutive failed attempts before giving up (status 'closed'). */
  maxAttempts?: number;
  pingIntervalMs?: number;
  /** A ping unanswered for this long means the socket is dead. */
  pingTimeoutMs?: number;
}

/** Close code used locally when we abandon a socket that stopped answering pings. */
const DEAD_SOCKET = 4900;

export function defaultServerUrl(): string {
  const override = import.meta.env?.VITE_WS_URL as string | undefined;
  if (override) return override;
  const { protocol, host } = globalThis.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}/ws`;
}

function isServerMessage(v: unknown): v is ServerMessage {
  return typeof v === 'object' && v !== null && typeof (v as { t?: unknown }).t === 'string';
}

export class WsConnection implements GameConnection {
  readonly kind = 'ws' as const;
  /** Why the connection ended for good (fatal close or retries exhausted); null after close(). */
  closeInfo: { code: number; reason: string } | null = null;

  private readonly url: string;
  private readonly opts: Required<Omit<WsConnectionOptions, 'url'>>;
  private ws: WebSocket | null = null;
  private _status: ConnectionStatus = 'connecting';
  private readonly msgListeners = new Set<(msg: ServerMessage) => void>();
  private readonly statusListeners = new Set<(status: ConnectionStatus) => void>();
  private attempts = 0;
  private userClosed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  /** performance.now() of the oldest ping not followed by any server message. */
  private awaitingSince: number | null = null;
  private readonly onOnline = () => {
    if (this.reconnectTimer) this.open();
  };

  constructor(options: WsConnectionOptions = {}) {
    this.url = options.url ?? defaultServerUrl();
    this.opts = {
      baseDelayMs: options.baseDelayMs ?? 500,
      maxDelayMs: options.maxDelayMs ?? 8000,
      maxAttempts: options.maxAttempts ?? 20,
      pingIntervalMs: options.pingIntervalMs ?? 5000,
      pingTimeoutMs: options.pingTimeoutMs ?? 12_000,
    };
    globalThis.addEventListener?.('online', this.onOnline);
    this.open();
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encodeClientMessage(msg));
  }

  onMessage(fn: (msg: ServerMessage) => void): () => void {
    this.msgListeners.add(fn);
    return () => this.msgListeners.delete(fn);
  }

  onStatus(fn: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  close(): void {
    if (this.userClosed) return;
    this.userClosed = true;
    this.closeInfo = null;
    this.clearTimers();
    globalThis.removeEventListener?.('online', this.onOnline);
    const ws = this.detachSocket();
    try {
      ws?.close(CloseCode.normal, 'bye');
    } catch {
      // already closing
    }
    this.setStatus('closed');
  }

  private open(): void {
    this.clearTimers();
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      // Malformed URL: retrying cannot help.
      this.closeInfo = { code: 1015, reason: err instanceof Error ? err.message : 'Invalid server URL' };
      this.setStatus('closed');
      return;
    }
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      this.awaitingSince = null;
      this.pingTimer = setInterval(() => this.heartbeat(), this.opts.pingIntervalMs);
      this.setStatus('open');
    };
    ws.onmessage = (e: MessageEvent) => this.onFrame(e.data);
    ws.onclose = (e: CloseEvent) => {
      if (ws === this.ws) this.handleClose(e.code, e.reason);
    };
    ws.onerror = () => {}; // a close event always follows
    this.ws = ws;
  }

  private onFrame(data: unknown): void {
    if (!(data instanceof ArrayBuffer)) return; // the server only sends binary frames
    let msg: unknown;
    try {
      msg = decode(data);
    } catch (err) {
      console.warn('[net] undecodable frame', err);
      return;
    }
    if (!isServerMessage(msg)) return;
    // The server is talking to us: the link is alive and healthy.
    this.awaitingSince = null;
    this.attempts = 0;
    for (const fn of this.msgListeners) {
      try {
        fn(msg);
      } catch (err) {
        console.error(`[net] handler for "${msg.t}" failed`, err);
      }
    }
  }

  private heartbeat(): void {
    const now = performance.now();
    if (this.awaitingSince !== null && now - this.awaitingSince > this.opts.pingTimeoutMs) {
      // Half-open socket: abandon it without waiting for the browser's own timeout.
      const ws = this.detachSocket();
      try {
        ws?.close();
      } catch {
        // ignore
      }
      this.handleClose(DEAD_SOCKET, 'no response from server');
      return;
    }
    this.send({ t: 'ping', time: now });
    this.awaitingSince ??= now;
  }

  private handleClose(code: number, reason: string): void {
    this.ws = null;
    this.clearTimers();
    if (this.userClosed) return;
    if (FATAL_CLOSE_CODES.has(code)) {
      this.closeInfo = { code, reason };
      this.setStatus('closed');
      return;
    }
    if (this.attempts >= this.opts.maxAttempts) {
      this.closeInfo = { code, reason: reason || 'The server is unreachable.' };
      this.setStatus('closed');
      return;
    }
    const backoff = Math.min(this.opts.maxDelayMs, this.opts.baseDelayMs * 2 ** this.attempts);
    this.attempts++;
    this.setStatus('connecting');
    this.reconnectTimer = setTimeout(() => this.open(), backoff * (0.75 + Math.random() * 0.5));
  }

  private detachSocket(): WebSocket | null {
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
    }
    return ws;
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  private setStatus(status: ConnectionStatus): void {
    if (status === this._status) return;
    this._status = status;
    for (const fn of this.statusListeners) {
      try {
        fn(status);
      } catch (err) {
        console.error('[net] status handler failed', err);
      }
    }
  }
}
